import { useEffect, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import type { Key } from 'chessground/types';
import { Board, type BoardMoveEvent } from '@/chess/board';
import { StockfishEngine, type EvalResult } from '@/chess/engine';

const DEMO_FEN = 'r1bqkbnr/pp1ppppp/2n5/2p5/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq - 2 3';

/**
 * Page de démo MVP1.0 (route /chess-demo) : valide que Board + engine.ts sont
 * fonctionnels en prod. Pas exposée dans la nav, accessible via URL directe.
 */
export function ChessDemoPage() {
  const chessRef = useRef(new Chess(DEMO_FEN));
  const engineRef = useRef<StockfishEngine | null>(null);
  const [fen, setFen] = useState(DEMO_FEN);
  const [lastMove, setLastMove] = useState<[Key, Key] | null>(null);
  const [evaluation, setEvaluation] = useState<EvalResult | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [crossOriginOk] = useState(typeof self !== 'undefined' && self.crossOriginIsolated === true);

  useEffect(() => {
    console.log('[chess-demo] crossOriginIsolated =', crossOriginOk);
  }, [crossOriginOk]);

  useEffect(() => {
    return () => engineRef.current?.destroy();
  }, []);

  function legalMovesMap(): Map<Key, Key[]> {
    const map = new Map<Key, Key[]>();
    for (const move of chessRef.current.moves({ verbose: true })) {
      const list = map.get(move.from as Key) ?? [];
      list.push(move.to as Key);
      map.set(move.from as Key, list);
    }
    return map;
  }

  function handleMove({ from, to }: BoardMoveEvent) {
    const move = chessRef.current.move({ from, to, promotion: 'q' });
    if (move === null) return;
    setLastMove([from, to]);
    setFen(chessRef.current.fen());
  }

  async function handleAnalyze() {
    setAnalyzing(true);
    setEvaluation(null);
    try {
      if (!engineRef.current) {
        engineRef.current = new StockfishEngine();
        await engineRef.current.init();
      }
      const result = await engineRef.current.analyze(fen, 16);
      setEvaluation(result);
    } catch (e) {
      console.error('[chess-demo] analyze failed', e);
      setEvaluation({ depth: 0, scoreCp: null, scoreMate: null, pv: '', bestmove: null });
    } finally {
      setAnalyzing(false);
    }
  }

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-12 lg:px-12">
      <header>
        <p className="text-caption uppercase text-ink-300">Démo Board + Engine</p>
        <h1 className="font-display text-display-2 text-paper-50">Position d'ouverture italienne</h1>
        <p className="mt-2 text-body-sm text-ink-300">
          Glisser, cliquer ou clavier (e2e4 + Entrée). Le bouton lance Stockfish à profondeur 16.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr,560px]">
        <div className="flex flex-col gap-4 rounded-xl border border-ink-700 bg-ink-800 p-6">
          <p className="text-caption uppercase text-ink-300">Statut moteur</p>
          <ul className="space-y-2 text-body-sm">
            <li>
              crossOriginIsolated :{' '}
              <span className={crossOriginOk ? 'text-volt-500' : 'text-coral-500'}>
                {crossOriginOk ? 'true (multi-thread)' : 'false (single-thread fallback)'}
              </span>
            </li>
            <li>
              FEN : <code className="font-mono text-body-sm text-ink-300">{fen}</code>
            </li>
          </ul>
          <button
            type="button"
            onClick={handleAnalyze}
            disabled={analyzing}
            className="self-start rounded-lg bg-acid-500 px-5 py-2 font-display text-body text-ink-900 transition hover:opacity-90 disabled:opacity-50"
          >
            {analyzing ? 'Analyse…' : 'Analyser (depth 16)'}
          </button>
          {evaluation && (
            <div className="rounded-lg border border-ink-700 bg-ink-900 p-4 text-body-sm">
              <p>
                Profondeur : <strong>{evaluation.depth}</strong>
              </p>
              <p>
                Eval :{' '}
                <strong className="text-acid-500">
                  {evaluation.scoreMate !== null
                    ? `mat ${evaluation.scoreMate}`
                    : evaluation.scoreCp !== null
                    ? `${(evaluation.scoreCp / 100).toFixed(2)}`
                    : '?'}
                </strong>
              </p>
              <p>
                Bestmove :{' '}
                <code className="font-mono text-volt-500">{evaluation.bestmove ?? '?'}</code>
              </p>
              <p className="mt-2 text-ink-300">PV : {evaluation.pv}</p>
            </div>
          )}
        </div>

        <Board
          fen={fen}
          interactive
          {...(lastMove ? { lastMove } : {})}
          legalMoves={legalMovesMap()}
          turnColor={chessRef.current.turn() === 'w' ? 'white' : 'black'}
          onMove={handleMove}
        />
      </section>
    </main>
  );
}
